import { createContext, useState, useContext } from 'react';

const QuizContext = createContext();

export const useQuiz = () => useContext(QuizContext);

export const QuizProvider = ({ children }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [tipoDeBarriga, setTipoDeBarriga] = useState(null);

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = () => {
    setCurrentQuestion(prev => prev + 1);
  };

  const previousQuestion = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const calculateResult = () => {
    // Lógica para determinar o tipo de barriga baseado nas respostas
    const tipoBarriga = determinarTipoBarriga(answers);
    setTipoDeBarriga(tipoBarriga);
    setQuizCompleted(true);
  };

  const determinarTipoBarriga = (respostas) => {
    const { pergunta1, pergunta2, pergunta3, pergunta4 } = respostas;
    const temSintomas = pergunta3 && pergunta3.length > 0 && !pergunta3.includes('Nenhum desses');
    const temInflamacao = pergunta3?.includes('Inflamações (gastrite, celulite, rinite)');
    const temInchaco = pergunta3?.includes('Inchaço abdominal no fim do dia');
    const temBaixaEnergia = pergunta3?.includes('Baixa energia ou cansaço constante');
    const temAnsiedade = pergunta3?.includes('Ansiedade ou estresse elevado');
    const jaTentouMetodos = pergunta2 === 'Sim, já tentei várias vezes, mas sem resultados duradouros';
    const temPoucosSintomas = pergunta3?.length <= 1;
    
    // Tipo 1 - Pochete Inflamatória
    if (pergunta1 === 'Pequena pochete abaixo do umbigo' && 
        jaTentouMetodos && 
        (temInchaco || temBaixaEnergia || temInflamacao || temAnsiedade) &&
        (pergunta4 === 'Perder apenas a gordura localizada' || pergunta4 === 'Emagrecer e perder a barriga')) {
      return {
        tipo: 'Pochete Inflamatória',
        descricao: 'Esse tipo de gordura é uma das mais difíceis de perder, pois está diretamente ligada à inflamação corporal e ao desequilíbrio hormonal.',
        naoDeve: 'Dietas restritivas não vão te ajudar, e podem até piorar o quadro.',
        deve: 'A solução para você é uma Alimentação Inteligente e Natural, focada em alimentos anti-inflamatórios e estratégicos para reprogramar seu metabolismo em apenas 21 dias.'
      };
    } 
    // Tipo 2 - Barriga Metabólica
    else if (pergunta1 === 'Barriga arredondada e rígida' && 
             (temAnsiedade || temInflamacao || temBaixaEnergia) &&
             (pergunta4 === 'Emagrecer e perder a barriga' || pergunta4 === 'Tonificar e definir minha barriga')) {
      return {
        tipo: 'Barriga Metabólica',
        descricao: 'Esse tipo de barriga está diretamente relacionado a um desequilíbrio metabólico, causado principalmente pelo cortisol (hormônio do estresse) e resistência à insulina.',
        naoDeve: 'Exercícios intensos podem piorar seu quadro, aumentando ainda mais seu nível de estresse e dificultando a perda da barriga.',
        deve: 'Você precisa de uma estratégia que combine alimentação equilibrada com exercícios moderados para normalizar seus hormônios e reeducar seu metabolismo em 21 dias.'
      };
    } 
    // Tipo 3 - Barriga Avental
    else if (pergunta1 === 'Barriga flácida, tipo avental' && 
             jaTentouMetodos && 
             (temBaixaEnergia || temInchaco) &&
             (pergunta4 === 'Tonificar e definir minha barriga' || pergunta4 === 'Perder apenas a gordura localizada')) {
      return {
        tipo: 'Barriga Avental',
        descricao: 'Esse tipo de barriga está relacionado à flacidez da pele, perda de colágeno e fraqueza muscular, geralmente após mudanças bruscas de peso ou gravidez.',
        naoDeve: 'Apenas dietas e cardio não resolverão seu problema, pois você precisa de um trabalho específico na musculatura e pele.',
        deve: 'Você precisa de uma abordagem com alimentação rica em proteínas e colágeno, combinada com exercícios específicos para tonificação e recuperação da firmeza abdominal.'
      };
    } 
    // Tipo 4 - Pochete Sem Tentativas
    else if (pergunta1 === 'Pequena pochete abaixo do umbigo' && 
             pergunta2 === 'Não, nunca tentei nada específico' &&
             (pergunta3?.includes('Nenhum desses') || temPoucosSintomas || !temSintomas) &&
             (pergunta4 === 'Perder apenas a gordura localizada' || pergunta4 === 'Tonificar e definir minha barriga')) {
      return {
        tipo: 'Pochete Sem Tentativas',
        descricao: 'Você está em um estágio inicial e ideal para obter resultados rápidos, pois seu corpo ainda não passou por diversas dietas que poderiam prejudicar seu metabolismo.',
        naoDeve: 'Evite iniciar com dietas muito restritivas que prometem resultados milagrosos, pois elas podem prejudicar seu metabolismo a longo prazo.',
        deve: 'A solução perfeita para você é um protocolo simples e eficiente que combine alimentação balanceada com exercícios estratégicos para eliminar essa gordura localizada rapidamente.'
      };
    } 
    // Tipo 5 - Barriga Metabólica Inicial
    else if (pergunta1 === 'Barriga arredondada e rígida' && 
             pergunta2 === 'Não, nunca tentei nada específico' &&
             (pergunta3?.includes('Nenhum desses') || temPoucosSintomas || !temSintomas) &&
             pergunta4 === 'Emagrecer e perder a barriga') {
      return {
        tipo: 'Barriga Metabólica Inicial',
        descricao: 'Sua barriga arredondada indica um possível desequilíbrio metabólico em estágio inicial, que pode ser revertido facilmente com as estratégias corretas.',
        naoDeve: 'Evite soluções genéricas que não consideram seu tipo específico de metabolismo e acúmulo de gordura.',
        deve: 'O ideal para você é um protocolo que combine alimentação estratégica com exercícios moderados para acelerar seu metabolismo e promover a queima de gordura abdominal.'
      };
    } 
    // Tipo 6 - Avental Leve
    else if (pergunta1 === 'Barriga flácida, tipo avental' && 
             pergunta2 === 'Não, nunca tentei nada específico' &&
             (pergunta3?.includes('Nenhum desses') || temPoucosSintomas || !temSintomas) &&
             pergunta4 === 'Tonificar e definir minha barriga') {
      return {
        tipo: 'Avental Leve',
        descricao: 'Você apresenta um início de flacidez abdominal que pode ser facilmente revertido com os estímulos corretos para recuperar o tônus muscular e a firmeza da pele.',
        naoDeve: 'Exercícios abdominais tradicionais isolados não resolverão seu problema e podem até piorar a aparência da sua barriga.',
        deve: 'Você precisa de um protocolo específico que combine alimentação construtora com exercícios focados em recuperar o tônus muscular e estimular a produção de colágeno na região abdominal.'
      };
    } 
    // Caso padrão - Pochete Inflamatória (por ser mais comum)
    else {
      return {
        tipo: 'Pochete Inflamatória',
        descricao: 'Esse tipo de gordura é uma das mais difíceis de perder, pois está diretamente ligada à inflamação corporal e ao desequilíbrio hormonal.',
        naoDeve: 'Dietas restritivas não vão te ajudar, e podem até piorar o quadro.',
        deve: 'A solução para você é uma Alimentação Inteligente e Natural, focada em alimentos anti-inflamatórios e estratégicos para reprogramar seu metabolismo em apenas 21 dias.'
      };
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setQuizCompleted(false);
    setTipoDeBarriga(null);
  };

  return (
    <QuizContext.Provider value={{
      currentQuestion,
      answers,
      quizCompleted,
      tipoDeBarriga,
      handleAnswer,
      nextQuestion,
      previousQuestion,
      calculateResult,
      resetQuiz
    }}>
      {children}
    </QuizContext.Provider>
  );
}; 